import { ExecutionResult } from 'graphql';
import { ofType } from 'redux-observable';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { closeCommonPriceEditor, updateCommonPriceEditorIsInProgressData } from '../../actions/commonPriceEditor';
import {
    CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS,
    changeCopiedRetargetingRulesTooltipStatus,
    ChangeCopiedRetargetingRulesTooltipStatus,
    COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD,
    CopyRetargetingRulesInfoToClipboard,
    deleteEditCellData,
    DeteleEditCellData,
    resetGridData,
    updateEditCellData,
    UpdateEditCellData
} from '../../actions/grid-screen';
import {
    SAVE_RETARGETINGS_MASS_COMMON_PRICE_EDITOR,
    SaveRetargetingsMassCommonPriceEditor,
    UPDATE_RETARGETING_COMMON_PRICE_CELL,
    updateRetargeting,
    UpdateRetargeting,
    UpdateRetargetingCommonPriceCell
} from '../../actions/retargetingCommonPriceEditor';
import { awaitCommonData } from '../../operators';
import { selectGridSourceParams } from '../../selectors/gridSourceParams/gridSourceParams';
import { copyTextToClipboard } from '../../service/Clipboard/Clipboard';
import {
    getGridActionErrorMessage,
    showRetargetingActionNoticeMessage
} from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { CopyTooltipStatus, Retargeting } from '../../typings/domain';
import { DnaEpic } from '../../typings/epic';
import { RetargetingCommonPriceEditorMutation } from '../../typings/graphql-grid';
import { RetargetingsAction } from '../../typings/grid';
import { UserProfile } from '../../typings/userProfileRetargeting';

type UpdateRetargetingCommonPriceCellObservable = Observable<
    DeteleEditCellData | UpdateRetargeting | UpdateEditCellData
>;

export const updateRetargetingCommonPriceCellEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_RETARGETING_COMMON_PRICE_CELL),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: UpdateRetargetingCommonPriceCell): UpdateRetargetingCommonPriceCellObservable => {
                const state = store$.value;
                const sourceParams = selectGridSourceParams(state);

                const { login, view } = sourceParams;
                const { id, priceContext } = action.retargeting;

                const cellKey = {
                    ...action.cellKeyPartial,
                    view
                };

                return from(
                    deps.retargetingMutationExecutor.retargetingCommonPriceEditorMutation(login, priceContext, [id])
                ).pipe(
                    mergeMap((response: ExecutionResult<RetargetingCommonPriceEditorMutation>) => {
                        const { data } = response;
                        if (!data) {
                            throw new Error();
                        }

                        const { validationResult } = data.setBidsRetargeting;
                        if (validationResult && validationResult.errors.length !== 0) {
                            throw new Error();
                        }

                        return [deleteEditCellData(cellKey), updateRetargeting(action.retargeting)];
                    }),
                    catchError(() =>
                        of(
                            updateEditCellData(cellKey, {
                                isSaving: false,
                                error: getGridActionErrorMessage(
                                    RetargetingsAction.setRetargetingCommonPriceEditor,
                                    false
                                )
                            })
                        )
                    )
                );
            }
        )
    );
};

export const acceptMassCommonPriceChangesEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_RETARGETINGS_MASS_COMMON_PRICE_EDITOR),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: SaveRetargetingsMassCommonPriceEditor): Observable<Action> => {
                const state = store$.value;
                const sourceParams = selectGridSourceParams(state);
                const {
                    grid: {
                        gridData: { data },
                        selectionInfo
                    },
                    commonPriceEditor: { commonPriceValue, errors }
                } = state;
                const retargetingIds = selectionInfo[action.view];

                // TODO: убрать фильтрацию, проверка должна быть на беке
                // https://st.yandex-team.ru/DIRECT-99709
                const retargetingIdsEditable = retargetingIds.filter(retargetingId => {
                    const retargeting = data.find(element => element && element.id === retargetingId) as
                        | Retargeting
                        | UserProfile
                        | undefined;

                    if (!retargeting) {
                        return false;
                    }

                    const { isEditable, isPriceContextEnabled } = retargeting;

                    return Boolean(isEditable && isPriceContextEnabled);
                });

                if (errors.length) {
                    return of(updateCommonPriceEditorIsInProgressData(false));
                }

                return from(
                    deps.retargetingMutationExecutor.retargetingCommonPriceEditorMutation(
                        sourceParams.login,
                        commonPriceValue,
                        retargetingIdsEditable
                    )
                ).pipe(
                    mergeMap(
                        (response: ExecutionResult<RetargetingCommonPriceEditorMutation>): Observable<Action> => {
                            const responseData = response.data;
                            const showConditionIds =
                                responseData && responseData.setBidsRetargeting
                                    ? responseData.setBidsRetargeting.showConditionIds
                                    : [];
                            const successCount = showConditionIds.length;

                            if (!successCount) {
                                return throwError('error');
                            }

                            showRetargetingActionNoticeMessage(
                                RetargetingsAction.setRetargetingCommonPriceEditor,
                                successCount,
                                retargetingIds.length
                            );

                            return of(resetGridData(), closeCommonPriceEditor());
                        }
                    ),
                    catchError(() => {
                        showRetargetingActionNoticeMessage(RetargetingsAction.setRetargetingCommonPriceEditor, 0, 1);

                        return of(closeCommonPriceEditor());
                    })
                );
            }
        )
    );
};

const COPIED_TOOLTIP_TIMEOUT = 1000;

export const copyRetargetingRulesInfoToClipboardEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD),
        mergeMap(
            (action: CopyRetargetingRulesInfoToClipboard): Observable<Action> => {
                return from(copyTextToClipboard(action.text)).pipe(
                    mergeMap(
                        (): Observable<Action> => {
                            return of(changeCopiedRetargetingRulesTooltipStatus(CopyTooltipStatus.success));
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(changeCopiedRetargetingRulesTooltipStatus(CopyTooltipStatus.error));
                        }
                    )
                );
            }
        )
    );
};

export const changeCopiedRetargetingRulesTooltipStatusEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS),
        mergeMap(
            (
                action: ChangeCopiedRetargetingRulesTooltipStatus
            ): Observable<ChangeCopiedRetargetingRulesTooltipStatus> => {
                const { newStatus } = action;

                if (newStatus === CopyTooltipStatus.hidden) {
                    return EMPTY;
                }

                return of(changeCopiedRetargetingRulesTooltipStatus(CopyTooltipStatus.hidden)).delay(
                    COPIED_TOOLTIP_TIMEOUT
                );
            }
        )
    );
};
